import React, { useCallback, useId, useState } from "react";
import classNames from "classnames";
import "./COREAnchoredPopup.less";
import {
  isInPageContent,
  PAGE_POPUP_CLASS,
} from "./useScrollablePopupContainer";

const supportsAnchorPositioning =
  typeof CSS !== "undefined" &&
  typeof CSS.supports === "function" &&
  CSS.supports("anchor-name: --core-anchor");

// Positions a popup on its trigger with CSS anchor positioning, so the browser
// keeps it there as the trigger scrolls, natively. For a trigger in a
// container that scrolls internally (e.g. a CORESider) whose popup is too wide
// to be mounted inside it without being clipped: the popup stays on
// document.body, out of reach of anything that would clip it.
//
// Give `anchorStyle` to an element around the trigger and `popupClassName` to
// the popup, and call `onOpen` with the trigger as the popup opens. The
// anchoring itself is in COREAnchoredPopup.less. Without browser support
// `isAnchored` is false and the popup behaves as it always has.
export const useAnchoredPopup = (enabled: boolean) => {
  const isAnchored = enabled && supportsAnchorPositioning;
  // useId's ":r1:" isn't a valid dashed ident.
  const anchorName = `--core-anchor-${useId().replace(/:/g, "")}`;
  const [inPageContent, setInPageContent] = useState(false);

  const onOpen = useCallback(
    (trigger: HTMLElement | null | undefined) => {
      if (isAnchored && trigger) setInPageContent(isInPageContent(trigger));
    },
    [isAnchored]
  );

  return {
    isAnchored,
    anchorStyle: isAnchored
      ? ({ "--core-anchor": anchorName } as React.CSSProperties)
      : undefined,
    popupClassName: classNames({
      "core-anchored-popup": isAnchored,
      // Anchored, the popup follows its trigger under the page's top bar.
      [PAGE_POPUP_CLASS]: isAnchored && inPageContent,
    }),
    // antd's open/close animation transforms the popup, which would then
    // contain the anchored content and stop it from finding its anchor.
    transitionName: isAnchored ? "" : undefined,
    onOpen,
  };
};
